import {Field, Form, Formik} from 'formik'
import * as Yup from 'yup'

import 'flatpickr/dist/themes/airbnb.css'
import Flatpickr from 'react-flatpickr'
import InputMask from 'react-input-mask'
import Select from 'react-select'

import {ConnectedFocusError} from 'focus-formik-error'
import moment from 'moment'
import {useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'
import {currency} from 'src/feature/bite/helpers/currency'
import {useSendPaymentLink} from 'src/feature/items/context/useMutation'
import {useGetActiveList, useGetDetail} from '../context/useQuery'
import {usePackage} from '../hook'

import {useAtom} from 'jotai'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import BreadCrumb from 'src/_metronic/layout/components/breadcrumb'
import {Success} from 'src/feature/bite/helpers/notification'
import useRequestParams from 'src/feature/bite/hook/request'
import {listActiveFilterAtom} from '../store'
import ControlledInput from 'src/feature/bite/component/ControlledInput'

export default function SendPaymentLinkFormPackage() {
  const intl = useIntl()

  const {packageId} = useParams()
  const [filter, setFilter] = useAtom(listActiveFilterAtom)

  const {isLoading: isSendingPaymentLink, mutateAsync} = useSendPaymentLink()
  const {isLoading: isLoadingPackageList} = useGetActiveList()
  const {isLoading: isLoadingPackageDetail, refetch} = useGetDetail()
  const {listActivePackages, detail} = usePackage()
  const [paymentUrl, setPaymentUrl] = useState('')
  const navigate = useNavigate()
  const [isGenerating, setIsGenerating] = useState(false)
  const {auth, headers} = useRequestParams()

  const paymentChannelOptions = [
    {value: '1', label: 'Email', icon: 'fa fa-envelope fs-4x text-dark'},
  ]

  const validSchema = Yup.object({
    itemID: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    quantity: Yup.number()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: 1})),
    channel: Yup.array()
      .of(Yup.string())
      .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    firstName: Yup.string().when(['linkType'], ([linkType], schema) => {
      if (linkType === 1) {
        return schema.required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      }
      return schema
    }),
    lastName: Yup.string().when(['linkType'], ([linkType], schema) => {
      if (linkType === 1) {
        return schema.required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      }
      return schema
    }),
    mobilePhoneNumber: Yup.string().when(['linkType'], ([linkType], schema) => {
      if (linkType === 1) {
        return schema.required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      }
      return schema
    }),
    paymentLinkName: Yup.string().when(['linkType'], ([linkType], schema) => {
      if (linkType === 1) {
        return schema.required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      }
      return schema
    }),
    linkType: Yup.number()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: 1})),
    expiryDate: Yup.string().when(['linkType'], ([linkType], schema) => {
      if (linkType === 1) {
        return schema.required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      }
      return schema.nullable()
    }),
    emailAddress: Yup.string().when(['linkType'], ([linkType], schema) => {
      if (linkType === 1) {
        return schema
          .email(intl.formatMessage({id: 'GLOBAL.VALIDATION.EMAIL'}))
          .max(100, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 100}))
          .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      }
      return schema
    }),
  })

  const initialValues = {
    itemID: packageId,
    quantity: 1,
    channel: ['1'],
    firstName: '',
    lastName: '',
    emailAddress: '',
    mobilePhoneNumber: '',
    paymentLinkName: '',
    mobileCountryCode: '+971',
    expiryDate: '',
    linkType: 1,
  }

  const onSubmit = async (values, {setSubmitting, resetForm}) => {
    setIsGenerating(true)
    let _channel

    if (values.channel.length === paymentChannelOptions.length) _channel = 4
    else if (values.channel.includes('1') && !values.channel.includes('2')) _channel = 1
    else if (values.channel.includes('2') && !values.channel.includes('1')) _channel = 2
    else if (values.channel.includes('1') && values.channel.includes('2')) _channel = 4

    if (values.linkType === 2) {
      values.expiryDate = moment(detail?.endDate).format('DD-MM-YYYY HH:mm')
      values.paymentLinkName = `${detail?.packageName}`
    }

    const phoneNumberRemovedCountryCode = values.mobilePhoneNumber
      ?.split(' ')
      .slice(1)
      .join(' ')
      .replace(/ /g, '')

    const paymentUrlResponse = await mutateAsync({
      ...values,
      channel: _channel || 3,
      mobileCountryCode: values.mobileCountryCode?.replace('+', ''),
      mobilePhoneNumber: phoneNumberRemovedCountryCode,
    })

    if (!paymentUrlResponse) {
      setSubmitting(false)
      setIsGenerating(false)
      return
    }

    if (values.linkType === 2) await refetch()
    if (values.linkType === 1) resetForm()

    setPaymentUrl(paymentUrlResponse?.response?.url)
    setSubmitting(false)

    navigator?.clipboard?.writeText(paymentUrlResponse?.response?.url)
    toast.success(intl.formatMessage({id: 'SEND_PAYMENT_LINK.URL_SUCCESS'}))

    setIsGenerating(false)

    if (values.channel.includes('3')) {
      window.open(`https://wa.me/send?text=${paymentUrlResponse?.response?.url}`, '_blank')
    }
  }

  const packageSelect =
    listActivePackages?.dataList?.map((item) => ({
      value: item.id,
      label: item.packageName,
    })) || []

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validSchema}
      onSubmit={onSubmit}
    >
      {(prop) => {
        return (
          <>
            <div className='d-flex gap-5 align-items-center'>
              <button
                className='btn btn-primary btn-sm'
                onClick={() => {
                  navigate('/packages')
                }}
              >
                <i className='fa fa-arrow-left'></i>
              </button>
              <BreadCrumb currentPage={intl.formatMessage({id: 'ITEM.SEND_PAYMENT_LINK'})} />
            </div>
            <Form className='form d-flex flex-column flex-lg-row fv-plugins-bootstrap5 fv-plugins-framework mt-10'>
              <ConnectedFocusError />
              <div className='d-flex flex-column flex-row-fluid gap-7 gap-lg-12'>
                <div className='tab-content'>
                  <div
                    className='tab-pane fade active show'
                    id='kt_ecommerce_add_product_general'
                    role='tabpanel'
                  >
                    <div className='d-flex flex-column gap-7 gap-lg-10'>
                      <div className='card card-flush py-4'>
                        <div className='card-header'>
                          <div className='card-title m-0'>
                            <div>
                              <h3 className='fw-bold mb-2 mt-5'>
                                {intl.formatMessage({id: 'ITEM.SEND_PAYMENT_LINK'})}
                              </h3>
                              <p className='text-muted fw-normal fs-6'>
                                Generate and send a secure payment link for a membership.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className='card-body'>
                          <div className='row'>
                            <div className='col-12 col-sm-6'>
                              <div className='fv-row fv-plugins-icon-container'>
                                <label className='required form-label'>
                                  Select Membership
                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip>
                                        Choose the membership for which the payment link will be
                                        generated.
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle text-primary ms-1'></i>
                                  </OverlayTrigger>
                                </label>
                                <Select
                                  id='itemID'
                                  name='itemID'
                                  isSearchable
                                  className='custom-select'
                                  options={packageSelect}
                                  placeholder={intl.formatMessage({
                                    id: 'ITEM.SEND_PAYMENT_LINK.SELECT_ITEM',
                                  })}
                                  value={packageSelect.find((s) => s.value === prop.values.itemID)}
                                  onChange={async (value) => {
                                    prop.setFieldValue('itemID', value?.value)
                                    navigate(`/packages/${value?.value}/send-payment-link`)
                                  }}
                                  isLoading={isLoadingPackageList || isLoadingPackageDetail}
                                />
                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.itemID && prop.touched.itemID && (
                                    <div className='fv-help-block'>{prop.errors.itemID}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='card card-flush py-4'>
                        <div className='card-header'>
                          <div className='card-title m-0'>
                            <div>
                              <h3 className='fw-bold mb-2 mt-5'>Choose your payment link type</h3>
                            </div>
                          </div>
                        </div>

                        <div className='card-body d-flex flex-column gap-10'>
                          <div className='row'>
                            <div className='d-flex flex-column gap-5'>
                              <div className='col-12'>
                                <div className='d-flex align-items-center gap-3'>
                                  <Field
                                    type='radio'
                                    name='linkType'
                                    value={1}
                                    id='linkTypeSpecificCustomer'
                                    className='form-check-input'
                                    checked={prop.values.linkType === 1}
                                    onChange={() => {
                                      prop.setFieldValue('linkType', 1)
                                      setPaymentUrl('')
                                    }}
                                  />
                                  <div className=''>
                                    <b className='fs-5 p-0 m-0'>
                                      Send a payment link to a specific customer
                                    </b>
                                    <p className='p-0 m-0'>
                                      The payment link will be delivered directly to the customer’s
                                      email address.
                                    </p>
                                  </div>
                                </div>
                              </div>

                              <div className='col-12 mt-3'>
                                <div className='d-flex align-items-center gap-3'>
                                  <Field
                                    type='radio'
                                    name='linkType'
                                    value={2}
                                    id='linkTypeReusable'
                                    className='form-check-input'
                                    checked={prop.values.linkType === 2}
                                    onChange={() => {
                                      prop.setFieldValue('linkType', 2)
                                      setPaymentUrl('')
                                    }}
                                  />
                                  <div className=''>
                                    <b className='fs-5 p-0 m-0'>
                                      Generate a reusable payment link that anyone can access
                                    </b>
                                    <p className='p-0 m-0'>
                                      The payment link can be copied, shared, or distributed via
                                      social channels.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {prop.errors.linkType && prop.touched.linkType && (
                            <div className='row'>
                              <div className='fv-help-block'>{prop.errors.linkType}</div>
                            </div>
                          )}
                        </div>
                      </div>

                      {prop.values.linkType === 1 && (
                        <div className='card card-flush py-4'>
                          <div className='card-header'>
                            <div className='card-title m-0'>
                              <div>
                                <h3 className='fw-bold mb-2 mt-5'>Payment Details</h3>
                                <p className='text-muted fw-normal fs-6'>
                                  {intl.formatMessage({id: 'ITEM.PAYMENT_INFO.2'})}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className='card-body'>
                            <div className='row'>
                              <div className='col-6 col-md-4'>
                                <div className='fv-row fv-plugins-icon-container'>
                                  <label className='required form-label !text-[10px] md:!text-base'>
                                    {intl.formatMessage({id: 'MEMBER.DETAIL.OVERVIEW.FIRST_NAME'})}

                                    <span className='position-relative'>
                                      <OverlayTrigger
                                        placement='top'
                                        overlay={
                                          <Tooltip>
                                            Enter the first name of the recipient for the payment
                                            link.
                                          </Tooltip>
                                        }
                                      >
                                        <i className='bi bi-info-circle text-primary ms-1'></i>
                                      </OverlayTrigger>
                                    </span>
                                  </label>

                                  <ControlledInput
                                    type='text'
                                    name='firstName'
                                    maxLength={50}
                                    className='form-control mb-2'
                                    placeholder={intl.formatMessage({
                                      id: 'MEMBER.DETAIL.OVERVIEW.FIRST_NAME',
                                    })}
                                    value={prop.values.firstName}
                                    setFieldValue={prop.setFieldValue}
                                  />

                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.firstName && prop.touched.firstName && (
                                      <div className='fv-help-block'>{prop.errors.firstName}</div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className='col-6 col-md-4'>
                                <div className='fv-row fv-plugins-icon-container'>
                                  <label className='required form-label !text-[10px] md:!text-base'>
                                    {intl.formatMessage({id: 'MEMBER.DETAIL.OVERVIEW.LAST_NAME'})}

                                    <span className='position-relative'>
                                      <OverlayTrigger
                                        placement='top'
                                        overlay={
                                          <Tooltip>Enter the last name of the recipient.</Tooltip>
                                        }
                                      >
                                        <i className='bi bi-info-circle text-primary ms-1'></i>
                                      </OverlayTrigger>
                                    </span>
                                  </label>

                                  <ControlledInput
                                    type='text'
                                    name='lastName'
                                    maxLength={50}
                                    className='form-control mb-2'
                                    placeholder={intl.formatMessage({
                                      id: 'MEMBER.DETAIL.OVERVIEW.LAST_NAME',
                                    })}
                                    value={prop.values.lastName}
                                    setFieldValue={prop.setFieldValue}
                                  />

                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.lastName && prop.touched.lastName && (
                                      <div className='fv-help-block'>{prop.errors.lastName}</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className='row mt-5'>
                              <div className='col-6 col-md-4'>
                                <div className='fv-row fv-plugins-icon-container'>
                                  <label className='required form-label !text-[10px] md:!text-base'>
                                    {intl.formatMessage({id: 'AUTH.BECOME_MEMBER.PHONE'})}

                                    <span className='position-relative'>
                                      <OverlayTrigger
                                        placement='top'
                                        overlay={
                                          <Tooltip>
                                            Provide the recipient’s phone number to send the payment
                                            link.
                                          </Tooltip>
                                        }
                                      >
                                        <i className='bi bi-info-circle text-primary ms-1'></i>
                                      </OverlayTrigger>
                                    </span>
                                  </label>

                                  <InputMask
                                    mask={'+\\9\\7\\1\\ 99 999 9999'}
                                    name='mobilePhoneNumber'
                                    className='form-control'
                                    type='phoneNumber'
                                    placeholder={intl.formatMessage({
                                      id: 'AUTH.BECOME_MEMBER.PHONE',
                                    })}
                                    value={prop.values.mobilePhoneNumber}
                                    onChange={(e) => {
                                      prop.setFieldValue('mobilePhoneNumber', e.target.value)
                                    }}
                                  />

                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.mobilePhoneNumber &&
                                      prop.touched.mobilePhoneNumber && (
                                        <div className='fv-help-block'>
                                          {prop.errors.mobilePhoneNumber}
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </div>

                              <div className='col-6 col-md-4'>
                                <div className='fv-row fv-plugins-icon-container'>
                                  <label className='required form-label !text-[10px] md:!text-base'>
                                    {intl.formatMessage({
                                      id: 'AUTH.BECOME_MEMBER.EMAIL',
                                    })}

                                    <span className='position-relative'>
                                      <OverlayTrigger
                                        placement='top'
                                        overlay={
                                          <Tooltip>
                                            Enter the recipient’s email address if email is selected
                                            as the delivery method.
                                          </Tooltip>
                                        }
                                      >
                                        <i className='bi bi-info-circle text-primary ms-1'></i>
                                      </OverlayTrigger>
                                    </span>
                                  </label>

                                  <Field
                                    type='text'
                                    name='emailAddress'
                                    className='form-control mb-2'
                                    placeholder={intl.formatMessage({
                                      id: 'AUTH.BECOME_MEMBER.EMAIL',
                                    })}
                                    value={prop.values.emailAddress}
                                    onChange={(e) =>
                                      prop.setFieldValue('emailAddress', e.target.value)
                                    }
                                  />

                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.emailAddress && prop.touched.emailAddress && (
                                      <div className='fv-help-block'>
                                        {prop.errors.emailAddress}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className='row mt-5'>
                              <div className='col-6 col-md-4'>
                                <div className='fv-row fv-plugins-icon-container'>
                                  <label className='required form-label !text-[10px] md:!text-base'>
                                    {intl.formatMessage({id: 'ITEM.PAYMENT_LINK.NAME'})}

                                    <span className='position-relative'>
                                      <OverlayTrigger
                                        placement='top'
                                        overlay={
                                          <Tooltip>
                                            Assign a name to the payment link for reference and
                                            tracking.
                                          </Tooltip>
                                        }
                                      >
                                        <i className='bi bi-info-circle text-primary ms-1'></i>
                                      </OverlayTrigger>
                                    </span>
                                  </label>
                                  <Field
                                    type='text'
                                    id='paymentLinkName'
                                    name='paymentLinkName'
                                    className='form-control mb-2'
                                    placeholder={intl.formatMessage({
                                      id: 'ITEM.PAYMENT_LINK.NAME',
                                    })}
                                    value={prop.values.paymentLinkName}
                                    onChange={(e) =>
                                      prop.setFieldValue('paymentLinkName', e.target.value)
                                    }
                                  />
                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.paymentLinkName &&
                                      prop.touched.paymentLinkName && (
                                        <div className='fv-help-block'>
                                          {prop.errors.paymentLinkName}
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </div>

                              <div className='col-6 col-md-4'>
                                <div className='fv-row fv-plugins-icon-container'>
                                  <label className='required form-label !text-[10px] md:!text-base'>
                                    {intl.formatMessage({id: 'ITEM.PAYMENT_LINK.VALIDITY'})}

                                    <span className='position-relative'>
                                      <OverlayTrigger
                                        placement='top'
                                        overlay={
                                          <Tooltip>
                                            Set an expiration date and time for the payment link to
                                            ensure timely transactions.
                                          </Tooltip>
                                        }
                                      >
                                        <i className='bi bi-info-circle text-primary ms-1'></i>
                                      </OverlayTrigger>
                                    </span>
                                  </label>

                                  <Flatpickr
                                    className='form-control'
                                    options={{
                                      disableMobile: true,
                                      enableTime: true,
                                      dateFormat: 'd-m-Y H:i',
                                      minDate: moment().add(10, 'days').format('DD-MM-YYYY HH:mm'),
                                    }}
                                    placeholder='DD-MM-YYYY HH:mm'
                                    name='expiryDate'
                                    id='expiryDate'
                                    value={moment(prop.values.expiryDate).format(
                                      'DD-MM-YYYY HH:mm'
                                    )}
                                    onChange={(date) => {
                                      const _date = moment(date[0]).format('DD-MM-YYYY HH:mm')
                                      prop.setFieldValue('expiryDate', _date)
                                    }}
                                  />

                                  <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                    {prop.errors.expiryDate && prop.touched.expiryDate && (
                                      <div className='fv-help-block'>{prop.errors.expiryDate}</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className='row mt-5'>
                              <div className='col-12'>
                                <b className='fs-5'>
                                  {intl.formatMessage({id: 'LABEL.PAYMENT.LINK'})}:
                                </b>
                              </div>
                              {paymentUrl !== '' ? (
                                <div className='col-12 d-flex align-items-center gap-2'>
                                  <a href={paymentUrl} target='_blank' rel='noreferrer'>
                                    {paymentUrl}
                                  </a>

                                  <div className='d-flex align-items-center gap-3'>
                                    <i
                                      className='fa-regular fa-copy cursor-pointer'
                                      onClick={() => {
                                        navigator?.clipboard?.writeText(paymentUrl)
                                        Success('Payment link copied to clipboard')
                                      }}
                                    ></i>
                                  </div>
                                </div>
                              ) : (
                                <div className='col-12'>
                                  <span className='fs-5'>
                                    {intl.formatMessage({id: 'ITEM.PAYMENT.LINK.NOT_CREATED'})}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className='row mt-5'>
                              <div className='col-12'>
                                <b className='fs-5'>Total Price:</b>
                              </div>
                              <div className='col-12'>
                                <span className='my-5'>
                                  {currency(detail?.unitPrice * (prop.values.quantity || 1) || 0)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className='d-flex justify-content-end px-10 pb-5'>
                            <button
                              type='submit'
                              className='btn btn-dark'
                              disabled={
                                isSendingPaymentLink ||
                                isGenerating ||
                                isLoadingPackageDetail ||
                                !listActivePackages?.dataList?.some(
                                  (item) => item.id === prop.values.itemID
                                ) ||
                                listActivePackages?.dataList?.find(
                                  (item) => item.id === prop.values.itemID
                                )?.paymentLinkEnable === false
                              }
                            >
                              {isSendingPaymentLink || isGenerating || isLoadingPackageDetail ? (
                                <span className='indicator-label'>
                                  {intl.formatMessage({id: 'PLEASE_WAIT'})}{' '}
                                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                              ) : (
                                <span className='indicator-label'>
                                  {intl.formatMessage({id: 'ITEM.SEND_PAYMENT_LINK'})}
                                </span>
                              )}
                            </button>
                          </div>
                        </div>
                      )}

                      {prop.values.linkType === 2 && (
                        <div className='card card-flush py-4'>
                          <div className='card-header'>
                            <div className='card-title m-0'>
                              <div>
                                <h3 className='fw-bold mb-2 mt-5'>Details</h3>
                                <p className='text-muted fw-normal fs-6'>
                                  The customer will enter their full name, phone number, and email
                                  address before completing the payment. The link stays active until
                                  the membership or product expires.
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className='card-body'>
                            <div className='row'>
                              <div className='col-12'>
                                <b className='fs-5'>Total Price:</b>
                              </div>
                              <div className='col-12'>
                                <span className='my-5'>
                                  {currency(detail?.unitPrice * (prop.values.quantity || 1) || 0)}
                                </span>
                              </div>
                            </div>

                            <div className='row mt-5'>
                              <div className='col-12'>
                                <b className='fs-5'>
                                  {intl.formatMessage({id: 'LABEL.PAYMENT.LINK'})}:
                                </b>
                              </div>
                              {detail?.genericPaymentLink !== '' && !!detail?.genericPaymentLink ? (
                                <div className='col-12 d-flex align-items-center gap-2'>
                                  <a
                                    href={detail?.genericPaymentLink || ''}
                                    target='_blank'
                                    rel='noreferrer'
                                  >
                                    {detail?.genericPaymentLink}
                                  </a>

                                  <div className='d-flex align-items-center gap-3'>
                                    <i
                                      className='fa-regular fa-copy cursor-pointer'
                                      onClick={() => {
                                        navigator?.clipboard?.writeText(
                                          detail?.genericPaymentLink || ''
                                        )
                                        Success('Payment link copied to clipboard')
                                      }}
                                    ></i>
                                  </div>
                                </div>
                              ) : (
                                <div className='col-12'>
                                  <span className='fs-5'>
                                    {intl.formatMessage({id: 'ITEM.PAYMENT.LINK.NOT_CREATED'})}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className='d-flex justify-content-end px-10 pb-5'>
                            <button
                              type='submit'
                              className='btn btn-dark'
                              disabled={
                                isSendingPaymentLink ||
                                isGenerating ||
                                isLoadingPackageDetail ||
                                (detail?.genericPaymentLink !== '' &&
                                  !!detail?.genericPaymentLink) ||
                                listActivePackages?.dataList?.find(
                                  (item) => item.id === prop.values.itemID
                                )?.paymentLinkEnable === false
                              }
                            >
                              {isSendingPaymentLink || isGenerating || isLoadingPackageDetail ? (
                                <span className='indicator-label'>
                                  {intl.formatMessage({id: 'PLEASE_WAIT'})}{' '}
                                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                              ) : (
                                <span className='indicator-label'>Create Payment Link</span>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          </>
        )
      }}
    </Formik>
  )
}
